import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import 'moment-timezone';

import { recuperarDetallSetmanaActual } from '../../actions/controlPresenciaActions';
import { ContadorJornadaHoresSetmana } from './ContadorJornadaHoresSetmana';
import { ResumTotalHoresSetmana } from './ResumTotalHoresSetmana';
import { convertirSegonsAMinuts, convertirHoresaSegons, convertirSegonsaHoresSenseFormat24 } from '../../Helpers/funcionsDates';

// ARQUITECTURA NOVA: 1 crida GET /setmana/actual/detall en lloc de 2 crides separades
// Llegeix de cp.detallSetmana en lloc de cp.llistatFixatgesSetmana + cp.llistatIncidenciesSetmana
//
// CORRECCIONS respecte l'original:
// 1. 2 crides separades (fixatges + incidencies) -> 1 crida unificada
// 2. useEffect re-disparava amb incidenciaRestaHores/incidenciaRestaHoresFiJornada com a deps
//    -> ara depèn de msgOk via useRef (igual que DetallDiaScreen) per refrescar
//    despres de guardar una operació sense re-fer fetch en obrir formularis

export const HoresTotalsSetmana = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('generic');

  const { auth, configUsuari, cp, ui } = useSelector(
    state => ({
      auth: state.auth,
      configUsuari: state.configUsuari,
      cp: state.cp,
      ui: state.ui
    }),
    shallowEqual
  );

  // 1 sola crida al muntar (o quan canvia el token).
  // Les actualitzacions post-escriptura les gestiona cpActualitzarDiaDetallSetmana
  // directament al store -> els useMemo de ResumTotalHoresSetmana recalculen sols.
  // NO afegim incidenciaRestaHores com a dep: el formulari es tanca síncronament
  // però recuperarDetallDiaComplet és async -> race condition garantida.
  useEffect(() => {
    if (!auth?.tokenJwt) return;
    dispatch(recuperarDetallSetmanaActual(auth.tokenJwt));
  }, [auth?.tokenJwt, dispatch]);

  // Càlculs memoitzats: suma computHores + llista de dies
  const { sumaComputHores, computHores, computMinuts, diesSetmanaLlista } = useMemo(() => {
    if (!configUsuari?.contingut || !cp?.detallSetmana?.fixatges_setmanals) return {};

    let suma = 0;
    const diesLlista = cp.detallSetmana.fixatges_setmanals.map(d => {
      if (d.esLaborable && ![1, 2].includes(d.idTipusAlerta) && d.dia >= configUsuari.contingut.iniciControlPresencia) {
        suma += d.computHores / 5 / 60;
      }
      return (
        <ContadorJornadaHoresSetmana
          key={d.dia}
          diaAmbFixatges={d}
          hores={d.computHores / 60}
        />
      );
    });

    const segs = convertirHoresaSegons(suma);
    return {
      sumaComputHores: suma,
      computHores:     convertirSegonsaHoresSenseFormat24(segs * 1000),
      computMinuts:    convertirSegonsAMinuts(segs * 1000),
      diesSetmanaLlista: diesLlista
    };
  }, [configUsuari?.contingut, cp?.detallSetmana]);

  if (!configUsuari?.contingut || !cp?.detallSetmana) {
    return <p>Recuperant dades de config usuari...</p>;
  }

  return (
    <div className="col-md-12">
      <h5 className="mt-3">
        {t('ResumHores.total')} {computHores} {t('Generics.hores')} {t('Generics.caracIoY')} {computMinuts} {t('Generics.minuts')}
      </h5>
      <p>
        {t('ResumHores.resum')}{' '}
        <Moment format="DD/MM/YYYY" date={cp.detallSetmana.dia_inici_setmana} />
        {' al '}
        <Moment format="DD/MM/YYYY" date={cp.detallSetmana.dia_final_setmana} />
      </p>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('Generics.dia')}</th>
              <th scope="col">{t('Generics.hores')}</th>
            </tr>
          </thead>
          <tbody>{diesSetmanaLlista}</tbody>
        </table>
      </div>

      <ResumTotalHoresSetmana hores={sumaComputHores} />
    </div>
  );
};